import type { TransportAssignment } from "@/models/transport-vehicle/transport-assignment";

/** De hoogste laag waarop een transport van dit voertuig staat. */
export const maximumTransportLayer = (transports?: TransportAssignment[]) =>
  (transports && transports.length > 0)
    ? Math.max(...transports.map(t => t.layer ?? 0))
    : 0;

/**
 * De hoogte van één rij in de dagplanner. Alle drie de kolommen (voertuigen, tijdlijn, morgen) rekenen
 * hiermee, dus wie in de tijdlijn ruimte bijzet moet die hier ook meegeven — anders lopen de kolommen
 * verticaal uit elkaar.
 *
 * `extraHeight` is bewust een pixelwaarde en geen aantal lagen: een laag is 32px terwijl een kaart 56px
 * hoog is (transporten cascaderen dus over elkaar), en iets dat er níet over mag vallen is in lagen niet
 * uit te drukken.
 */
export const giveSwimlaneItemHeight = (
  additionalSwimlineHeight: number,
  transports?: TransportAssignment[],
  extraHeight = 0
) => {
  const maximumLayer = maximumTransportLayer(transports);
  const stackedHeight = maximumLayer * additionalSwimlineHeight + extraHeight;

  return { height: `calc(var(--dis-shape-height-7_0x) + ${stackedHeight}px + var(--dis-space-2_0x))` };
};

/**
 * Verdeelt de transporten van één voertuig over lagen zodat transporten die in tijd overlappen onder
 * elkaar komen te staan in plaats van over elkaar heen.
 */
export const assignLayers = (transportAssignments: TransportAssignment[]) => {
  const mapped = transportAssignments.map(item => {
    if (!item.transportStartDate || !item.transportLanes) {
      return { ...item, start: 0, end: 0 };
    }
    const start = item.transportStartDate;
    const totalDuration = item.transportLanes.reduce(
      // De pauze telt mee: hij zit niet in de rijtijd, maar de wagen is er wel mee bezig.
      (sum, lane) => sum + (lane.totalLocationDuration * 1000) + (lane.drivingDuration * 1000) + (lane.breakDuration * 1000),
      0
    );
    const end = start + totalDuration;
    return { ...item, start, end };
  });

  mapped.sort((a, b) => a.start - b.start);

  const layers: number[] = [];
  const layered = mapped.map(item => {
    let layerIndex = 0;

    // Find the first available layer where this item does not conflict
    while (layerIndex < layers.length && item.start < layers[layerIndex]) {
      layerIndex++;
    }

    // Assign this item to the layer and update its latest end time
    layers[layerIndex] = item.end;

    return { ...item, layer: layerIndex };
  });

  layered.sort((a, b) => {
    if (a.layer === b.layer) {
      return a.start - b.start;
    }
    return a.layer - b.layer;
  });

  return layered;
};

/**
 * Zet de laagverdeling op een compleet dagschema. Hoort bij het ophalen van het schema en niet bij de
 * weergave: elke plek die het schema herlaadt (datumwissel, afgeronde planrun) heeft de lagen nodig.
 */
export const assignScheduleLayers = <T extends { transports?: TransportAssignment[] }>(schedule: T[]) => {
  for (const item of schedule) {
    if (!item.transports) {
      continue;
    }

    item.transports = assignLayers(item.transports);
  }

  return schedule;
};
